import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'

import { Close } from '@/components/icons'
import { isIneligibleForProgram } from '@/pages/benefits/clubBenefitEligibility'
import { useClaimableBenefits, useClubBenefitPrograms } from '@/services/myBenefits'

type Props = {
  open: boolean
  onClose: () => void
  clubId: number
  clubName: string
}

/**
 * What a club offers, opened from its badge.
 *
 * A dialog rather than an inline expansion: the answer belongs to one row of a list, and growing
 * that row pushes every club below it down the page — the reader loses their place in the list to
 * read about one entry in it.
 *
 * Fetches only while open. The badge appears on every participating row, and asking each one what
 * it offers would be a request per row for an answer almost nobody opens.
 *
 * Mirrors `ClubBenefitOverlay.tsx` in the mobile app.
 */
export const ClubBenefitsModal = ({ open, onClose, clubId, clubName }: Props) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useClubBenefitPrograms(clubId, open)
  const { data: claimable } = useClaimableBenefits()

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/45" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Dialog.Title className="text-base font-semibold text-navy-900">
                      {t('benefits.clubBenefitsTitle')}
                    </Dialog.Title>
                    <p className="mt-0.5 text-sm text-gray-500">{clubName}</p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('benefits.close')}
                    className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Close className="h-5 w-5" />
                  </button>
                </div>

                {isLoading && <p className="py-6 text-sm text-gray-500">…</p>}
                {isError && <p className="py-6 text-sm text-gray-600">{t('benefits.clubBenefitsError')}</p>}
                {/* Empty is reachable: a club can leave a program between the list loading and the
                    badge being clicked, and saying so beats a blank panel that looks broken. */}
                {!isLoading && !isError && data?.length === 0 && (
                  <p className="py-6 text-sm text-gray-600">{t('benefits.clubBenefitsEmpty')}</p>
                )}

                <ul className="mt-3 space-y-2">
                  {(data ?? []).map((program) => {
                    const notHeld = isIneligibleForProgram(
                      (program.terms ?? []).map((term) => term.key),
                      claimable ?? []
                    )
                    // The worth rather than the program's name: it is the more specific true
                    // thing and already contains the name — "$25 Club Cash", not "Club Cash".
                    const worth = program.worth ?? program.label
                    return (
                      <li key={`${program.classUrn}:${worth}`} className="flex items-start gap-2">
                        <span
                          aria-hidden="true"
                          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                            notHeld ? 'bg-gray-300' : 'bg-[#3A79E0]'
                          }`}
                        />
                        <div>
                          <p className={`text-sm ${notHeld ? 'text-gray-500' : 'text-navy-900'}`}>{worth}</p>
                          {/* Dimmed rather than flagged red: not holding something is an ordinary
                              state, and a club page is not the place to look like an error. */}
                          {notHeld && <p className="text-xs text-gray-500">{t('benefits.clubNotEligible')}</p>}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
